import { MenuPrefService } from "../resource";
import {
    MENU_TREE,
    filterAccessibleTree,
    allMenuKeys
} from "../util/menu-config";

/**
 * menuPref — per-user sidebar menu preferences.
 *
 * CONTRACT (shared with Dev-BO-D who consumes it in the Sidebar component):
 *   state    : { hidden: string[], pinned: string[], loaded: boolean }
 *   getters  :
 *     hiddenMenus     -> string[]   keys the user chose to hide
 *     pinnedMenus     -> string[]   keys the user pinned (always shown)
 *     visibleTree     -> tree[]     MENU_TREE filtered to the current user's
 *                                    permissions (uses Auth/* getters)
 *     isMenuShown     -> (key) => boolean
 *                        The single helper BO-D calls per <sidebar-item>:
 *                        pinned wins, otherwise hidden hides it, default shown.
 *   actions  :
 *     load  -> GET  /api/v1/user/menu-prefs  (Data[0] envelope)
 *     save  -> PUT  /api/v1/user/menu-prefs  ({ hidden_menus, pinned_menus })
 */
const MenuPrefModule = {
    namespaced: true,
    state: {
        hidden: [],
        pinned: [],
        loaded: false
    },
    mutations: {
        SET_PREFS(state, { hidden, pinned }) {
            state.hidden = Array.isArray(hidden) ? hidden : [];
            state.pinned = Array.isArray(pinned) ? pinned : [];
            state.loaded = true;
        },
        SET_LOADED(state, v) {
            state.loaded = v;
        }
    },
    actions: {
        async load({ commit }) {
            try {
                const res = await MenuPrefService.Get();
                const rows = (res && res.data && res.data.Data) || [];
                const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : {};
                commit("SET_PREFS", {
                    hidden: row.hidden_menus || [],
                    pinned: row.pinned_menus || []
                });
                return row;
            } catch (err) {
                // Fail open: no prefs => everything visible.
                commit("SET_PREFS", { hidden: [], pinned: [] });
                throw err;
            }
        },
        async save({ commit }, { hidden, pinned }) {
            const payload = {
                hidden_menus: Array.isArray(hidden) ? hidden : [],
                pinned_menus: Array.isArray(pinned) ? pinned : []
            };
            const res = await MenuPrefService.Update(payload);
            commit("SET_PREFS", { hidden: payload.hidden_menus, pinned: payload.pinned_menus });
            return res;
        }
    },
    getters: {
        hiddenMenus(state) {
            return state.hidden;
        },
        pinnedMenus(state) {
            return state.pinned;
        },
        menuLoaded(state) {
            return state.loaded;
        },
        // Full canonical tree (unfiltered) — handy for tooling/tests.
        menuTree() {
            return MENU_TREE;
        },
        allMenuKeys() {
            return allMenuKeys();
        },
        // MENU_TREE filtered to what the current user is permitted to see.
        // Uses the SAME Auth/* getters the sidebar v-if relies on.
        visibleTree(state, getters, rootState, rootGetters) {
            const resolve = name => rootGetters["Auth/" + name];
            return filterAccessibleTree(resolve);
        },
        // The helper BO-D wires into each sidebar item. Pin wins over hide;
        // default (unknown key) is shown.
        isMenuShown(state) {
            return key => {
                if (state.pinned.indexOf(key) !== -1) return true;
                return state.hidden.indexOf(key) === -1;
            };
        },
        isMenuPinned(state) {
            return key => state.pinned.indexOf(key) !== -1;
        }
    }
};

export default MenuPrefModule;
